
import React, { useState, useMemo } from 'react';
import { User, Category, Expense } from '../types';
import { Settings, LogOut, AlertTriangle, Target, Edit2, Save, X, Trash2, Plus, Palette, Bell, Moon, Shield, FileText, ChevronRight, Download, Smartphone } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ProfileProps {
  currentUser: User;
  categories: Category[];
  monthlyExpenses: Expense[]; // Projected monthly data
  onUpdateUser: (user: User) => void;
  onUpdateCategories: (categories: Category[]) => void;
  onDeleteCategory: (id: string) => void;
  selectedDate: Date;
}

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981', 
  '#06b6d4', '#3b82f6', '#8b5cf6', '#d946ef', '#f43f5e', '#64748b'
];

export const Profile: React.FC<ProfileProps> = ({ currentUser, categories, monthlyExpenses, onUpdateUser, onUpdateCategories, onDeleteCategory, selectedDate }) => {
  const [editingBudget, setEditingBudget] = useState(false);
  const [tempBudget, setTempBudget] = useState(currentUser.monthlyBudget.toString());

  // Category Management State
  const [showCatModal, setShowCatModal] = useState(false);
  const [editingCatId, setEditingCatId] = useState<string | null>(null);
  const [catFormName, setCatFormName] = useState('');
  const [catFormLimit, setCatFormLimit] = useState('');
  const [catFormColor, setCatFormColor] = useState(PRESET_COLORS[0]);
  const [catFormAlert, setCatFormAlert] = useState(true);

  // Delete Confirmation State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);

  // Settings Modal State
  const [showSettings, setShowSettings] = useState(false);
  const [pushNotif, setPushNotif] = useState(true);
  const [biometrics, setBiometrics] = useState(false);

  // Calculate current month spending (Expenses only)
  const currentMonthExpenses = useMemo(() => {
    return monthlyExpenses.filter(e => e.type === 'expense');
  }, [monthlyExpenses]);

  const totalSpent = currentMonthExpenses.reduce((acc, curr) => acc + curr.amount, 0);
  const budgetProgress = currentUser.monthlyBudget > 0 ? (totalSpent / currentUser.monthlyBudget) * 100 : 0;

  const handleSaveBudget = () => {
    // Remove formatting before saving
    const numericValue = parseFloat(tempBudget.replace(/\./g, '').replace(',', '.')) || 0;
    onUpdateUser({ ...currentUser, monthlyBudget: numericValue });
    setEditingBudget(false);
  };

  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    if (!rawValue) {
      setTempBudget("");
      return;
    }
    const floatValue = parseFloat(rawValue) / 100;
    const formatted = floatValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setTempBudget(formatted);
  };

  const handleStartEditingBudget = () => {
    setEditingBudget(true); 
    setTempBudget(currentUser.monthlyBudget.toLocaleString("pt-BR", { minimumFractionDigits: 2 }));
  };

  const handleToggleDarkMode = () => {
     onUpdateUser({ ...currentUser, darkMode: !currentUser.darkMode });
  };

  // --- Category Management Handlers ---

  const openNewCategoryModal = () => {
    setEditingCatId(null);
    setCatFormName('');
    setCatFormLimit('');
    setCatFormColor(PRESET_COLORS[Math.floor(Math.random() * PRESET_COLORS.length)]);
    setCatFormAlert(true);
    setShowCatModal(true);
  };

  const openEditCategoryModal = (cat: Category) => {
    setEditingCatId(cat.id);
    setCatFormName(cat.name);
    setCatFormLimit(cat.budgetLimit.toLocaleString("pt-BR", { minimumFractionDigits: 2 }));
    setCatFormColor(cat.color);
    setCatFormAlert(cat.alertEnabled ?? true);
    setShowCatModal(true);
  };

  const handleCatLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    if (!rawValue) {
      setCatFormLimit("");
      return;
    }
    const floatValue = parseFloat(rawValue) / 100;
    const formatted = floatValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setCatFormLimit(formatted);
  };

  const handleSaveCategory = (e: React.FormEvent) => {
    e.preventDefault();
    if (!catFormName.trim()) return;

    const numericLimit = parseFloat(catFormLimit.replace(/\./g, '').replace(',', '.')) || 0;

    if (editingCatId) {
      // Edit existing
      const updatedCategories = categories.map(c => 
        c.id === editingCatId ? { 
          ...c, 
          name: catFormName, 
          budgetLimit: numericLimit,
          color: catFormColor,
          alertEnabled: catFormAlert
        } : c
      );
      onUpdateCategories(updatedCategories);
    } else {
      // Add new
      const newCategory: Category = {
        id: Math.random().toString(36).substr(2, 9),
        name: catFormName,
        budgetLimit: numericLimit,
        color: catFormColor,
        icon: 'Tag', // Default icon
        alertEnabled: catFormAlert
      };
      onUpdateCategories([...categories, newCategory]);
    }
    setShowCatModal(false);
  };

  const requestDeleteCategory = (id: string) => {
    setCategoryToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteCategory = () => {
    if (categoryToDelete) {
      onDeleteCategory(categoryToDelete);
    }
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  const handleLogout = async () => {
    if (confirm('Deseja realmente sair?')) {
      await supabase.auth.signOut();
      // App.tsx auth listener will handle redirect
    }
  };

  const handleExportCSV = () => {
    // Generate CSV content
    const headers = ['Data', 'Descrição', 'Categoria', 'Valor', 'Tipo', 'Status'];
    const rows = monthlyExpenses.map(e => {
        const cat = categories.find(c => c.id === e.categoryId)?.name || 'Sem categoria';
        const date = new Date(e.date).toLocaleDateString('pt-BR');
        const amount = e.amount.toFixed(2).replace('.', ',');
        const type = e.type === 'income' ? 'Entrada' : 'Saída';
        return [date, `"${e.description}"`, `"${cat}"`, amount, type, e.status];
    });

    const csvContent = [
        headers.join(';'),
        ...rows.map(r => r.join(';'))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `extrato_fiantro_${selectedDate.getMonth()+1}_${selectedDate.getFullYear()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-8 pb-10">
      {/* Header Profile */}
      <div className="flex flex-col items-center justify-center pt-8">
        <div className="relative">
          <div className="w-28 h-28 bg-slate-100 dark:bg-zinc-800 rounded-full mb-4 overflow-hidden border-4 border-white dark:border-zinc-800 shadow-xl">
            <img src={currentUser.avatarUrl || "https://picsum.photos/200/200"} alt="Profile" className="w-full h-full object-cover" />
          </div>
          <button className="absolute bottom-4 right-0 p-2 bg-brand-lime text-brand-dark rounded-full border-4 border-white dark:border-zinc-800 shadow-sm">
             <Edit2 size={16} />
          </button>
        </div>
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white">{currentUser.name}</h2>
        <p className="text-slate-400 font-medium">{currentUser.email}</p>
      </div>

      {/* Global Budget Section */}
      <div className="bg-brand-dark dark:bg-zinc-900 text-white rounded-[2rem] p-6 shadow-soft relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute -right-10 -top-10 w-32 h-32 bg-brand-lime opacity-10 rounded-full blur-2xl"></div>
        
        <div className="relative z-10">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-white/10 rounded-xl text-brand-lime">
                <Target size={20} />
              </div>
              <h3 className="font-bold text-lg">Meta Mensal</h3>
            </div>
            {editingBudget ? (
              <div className="flex gap-2">
                <button onClick={handleSaveBudget} className="p-2 bg-brand-lime text-brand-dark rounded-full"><Save size={16} /></button>
                <button onClick={() => setEditingBudget(false)} className="p-2 bg-white/10 text-white rounded-full"><X size={16} /></button>
              </div>
            ) : (
              <button onClick={handleStartEditingBudget} className="text-sm text-slate-400 hover:text-white underline">
                Editar
              </button>
            )}
          </div>

          {editingBudget ? (
            <div className="mb-4">
               <label className="text-xs text-slate-400 block mb-1">Novo limite total</label>
               <div className="flex items-center gap-2 bg-white/5 rounded-xl px-4 py-2 border border-white/10">
                 <span className="text-brand-lime font-bold">R$</span>
                 <input 
                   type="tel"
                   value={tempBudget} 
                   onChange={handleBudgetChange}
                   className="bg-transparent text-xl font-bold w-full focus:outline-none text-white"
                   autoFocus
                 />
               </div>
            </div>
          ) : (
            <div className="flex items-end gap-2 mb-2">
              <span className="text-3xl font-bold">R$ {currentUser.monthlyBudget.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              <span className="text-slate-400 text-sm mb-1.5">limite</span>
            </div>
          )}

          {/* Progress Bar Global */}
          <div className="mt-4">
            <div className="flex justify-between text-xs mb-1.5 text-slate-300">
              <span className="capitalize">{selectedDate.toLocaleString('pt-BR', { month: 'long' })}: R$ {totalSpent.toFixed(0)}</span>
              <span>{budgetProgress.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${budgetProgress > 100 ? 'bg-red-500' : budgetProgress > 80 ? 'bg-amber-400' : 'bg-brand-lime'}`} 
                style={{ width: `${Math.min(budgetProgress, 100)}%` }}
              ></div>
            </div>
            {budgetProgress > 90 && (
              <div className="flex items-center gap-2 mt-3 text-amber-400 text-xs font-bold bg-amber-400/10 p-2 rounded-lg border border-amber-400/20">
                <AlertTriangle size={14} />
                {budgetProgress > 100 ? 'Você excedeu seu orçamento mensal!' : 'Atenção: Você está próximo do limite mensal.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Categories Management */}
      <div>
        <div className="flex justify-between items-center mb-4 px-2">
           <h3 className="text-lg font-bold text-slate-800 dark:text-white">Categorias</h3>
           <button 
             onClick={openNewCategoryModal}
             className="flex items-center gap-1 text-xs font-bold bg-slate-900 dark:bg-zinc-800 text-white px-3 py-1.5 rounded-lg hover:bg-slate-700 dark:hover:bg-zinc-700 transition-colors"
           >
             <Plus size={14} /> Nova
           </button>
        </div>
        
        <div className="space-y-4">
          {categories.map(cat => {
            const catSpent = currentMonthExpenses.filter(e => e.categoryId === cat.id).reduce((acc, curr) => acc + curr.amount, 0);
            const catProgress = cat.budgetLimit > 0 ? (catSpent / cat.budgetLimit) * 100 : 0;
            const alertTriggered = cat.alertEnabled && catProgress >= 80;

            return (
              <div key={cat.id} className={`bg-white dark:bg-zinc-900 p-5 rounded-[1.5rem] shadow-sm border border-slate-100 dark:border-zinc-800 relative group transition-all ${alertTriggered ? 'ring-2 ring-amber-400/50' : ''}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg" style={{ backgroundColor: `${cat.color}20`, color: cat.color }}>
                       {cat.name.substring(0,2).toUpperCase()}
                    </div>
                    <div>
                       <div className="flex items-center gap-2">
                         <p className="font-bold text-slate-800 dark:text-slate-200">{cat.name}</p>
                         {cat.alertEnabled && <Bell size={12} className="text-slate-400" />}
                       </div>
                       <p className="text-xs text-slate-400">R$ {catSpent.toFixed(0)} / R$ {cat.budgetLimit.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</p>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1">
                     <button onClick={() => openEditCategoryModal(cat)} className="p-2 text-slate-300 hover:text-brand-dark dark:hover:text-white hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-full transition-colors">
                       <Edit2 size={16} />
                     </button>
                     <button onClick={() => requestDeleteCategory(cat.id)} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/20 rounded-full transition-colors">
                       <Trash2 size={16} />
                     </button>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-slate-100 dark:bg-zinc-800 rounded-full h-2 overflow-hidden mb-1">
                   <div 
                      className={`h-full rounded-full transition-all duration-500 ${catProgress > 100 ? 'bg-red-500' : (alertTriggered ? 'bg-amber-400' : 'bg-brand-lime')}`} 
                      style={{ backgroundColor: catProgress > 100 ? '#ef4444' : (alertTriggered ? '#fbbf24' : cat.color), width: `${Math.min(catProgress, 100)}%` }}
                   ></div>
                </div>
                
                {alertTriggered && (
                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-amber-500 mt-2 bg-amber-50 dark:bg-amber-500/10 p-1.5 rounded-lg w-fit">
                    <AlertTriangle size={12} />
                    <span>Atenção: {catProgress > 100 ? 'Limite excedido!' : '80% do limite atingido.'}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="w-full space-y-3 pt-4">
        <button 
          onClick={() => setShowSettings(true)}
          className="w-full py-4 bg-slate-50 dark:bg-zinc-900 rounded-2xl font-medium text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-zinc-800 flex items-center justify-between px-6 transition-colors"
        >
          <div className="flex items-center gap-3">
             <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-zinc-700 flex items-center justify-center text-slate-600 dark:text-slate-300">
               <Settings size={16} />
             </div>
             <span>Configurações do App</span>
          </div>
          <ChevronRight size={18} className="text-slate-400" />
        </button>
        
        <button 
          onClick={handleLogout}
          className="w-full py-4 bg-red-50 dark:bg-red-500/10 rounded-2xl font-bold text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20 flex items-center justify-between px-6 transition-colors"
        >
          <div className="flex items-center gap-3">
             <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
               <LogOut size={16} />
             </div>
             <span>Sair da Conta</span>
          </div>
        </button>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-zinc-900 w-full max-w-sm rounded-[2rem] p-6 animate-in zoom-in-95 duration-200 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">Configurações</h3>
              <button onClick={() => setShowSettings(false)} className="p-2 bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-slate-300 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-700">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Preferences */}
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Preferências</h4>
                <div className="space-y-1">
                  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-zinc-700 rounded-lg text-slate-600 dark:text-slate-300 shadow-sm"><Bell size={18} /></div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Notificações</span>
                    </div>
                    <div 
                      className={`w-11 h-6 rounded-full p-1 cursor-pointer transition-colors ${pushNotif ? 'bg-brand-lime' : 'bg-slate-300 dark:bg-zinc-600'}`}
                      onClick={() => setPushNotif(!pushNotif)}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${pushNotif ? 'translate-x-5' : ''}`}></div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-zinc-700 rounded-lg text-slate-600 dark:text-slate-300 shadow-sm"><Moon size={18} /></div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Modo Escuro</span>
                    </div>
                    <div 
                      className={`w-11 h-6 rounded-full p-1 cursor-pointer transition-colors ${currentUser.darkMode ? 'bg-brand-lime' : 'bg-slate-300 dark:bg-zinc-600'}`}
                      onClick={handleToggleDarkMode}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${currentUser.darkMode ? 'translate-x-5' : ''}`}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security */}
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Segurança</h4>
                <div className="space-y-1">
                  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-zinc-700 rounded-lg text-slate-600 dark:text-slate-300 shadow-sm"><Smartphone size={18} /></div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Face ID / Touch ID</span>
                    </div>
                    <div 
                      className={`w-11 h-6 rounded-full p-1 cursor-pointer transition-colors ${biometrics ? 'bg-brand-lime' : 'bg-slate-300 dark:bg-zinc-600'}`}
                      onClick={() => setBiometrics(!biometrics)}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${biometrics ? 'translate-x-5' : ''}`}></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800 rounded-xl cursor-pointer hover:bg-slate-100 dark:hover:bg-zinc-700">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-zinc-700 rounded-lg text-slate-600 dark:text-slate-300 shadow-sm"><Shield size={18} /></div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Alterar Senha</span>
                    </div>
                    <ChevronRight size={16} className="text-slate-400" />
                  </div>
                </div>
              </div>

              {/* Data */}
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Dados</h4>
                <div className="space-y-1">
                  <button 
                    onClick={handleExportCSV}
                    className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800 rounded-xl hover:bg-slate-100 dark:hover:bg-zinc-700 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-zinc-700 rounded-lg text-slate-600 dark:text-slate-300 shadow-sm"><Download size={18} /></div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Exportar Extrato (CSV)</span>
                    </div>
                    <ChevronRight size={16} className="text-slate-400" />
                  </button>

                  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-zinc-700 rounded-lg text-slate-600 dark:text-slate-300 shadow-sm"><FileText size={18} /></div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Termos de Uso</span>
                    </div>
                    <ChevronRight size={16} className="text-slate-400" />
                  </div>
                </div>
              </div>

              <div className="text-center pt-4">
                 <p className="text-xs text-slate-400 font-medium">Fiantro v1.0.0</p>
                 <p className="text-[10px] text-slate-300 dark:text-zinc-600">Feito com 💚</p>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCatModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-zinc-900 w-full max-w-sm rounded-[2rem] p-6 animate-in zoom-in-95 duration-200 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                {editingCatId ? 'Editar Categoria' : 'Nova Categoria'}
              </h3>
              <button onClick={() => setShowCatModal(false)} className="p-2 bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-slate-300 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-700">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveCategory} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Nome</label>
                <input 
                  type="text" 
                  value={catFormName}
                  onChange={(e) => setCatFormName(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-zinc-800 border-none rounded-xl font-medium text-slate-800 dark:text-white focus:ring-2 focus:ring-brand-lime outline-none"
                  placeholder="Ex: Alimentação"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Limite Mensal</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">R$</span>
                  <input 
                    type="tel" 
                    value={catFormLimit}
                    onChange={handleCatLimitChange}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-zinc-800 border-none rounded-xl font-medium text-slate-800 dark:text-white focus:ring-2 focus:ring-brand-lime outline-none"
                    placeholder="0,00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <Palette size={14} /> Cor
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setCatFormColor(color)}
                      className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${catFormColor === color ? 'border-slate-800 dark:border-white scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              {/* Alert Toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800 rounded-xl">
                 <div className="flex items-center gap-3">
                    <div className="p-2 bg-white dark:bg-zinc-700 rounded-lg text-amber-500 shadow-sm"><AlertTriangle size={18} /></div>
                    <div className="flex flex-col">
                       <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Alertar em 80%?</span>
                       <span className="text-[10px] text-slate-400">Avisar se o limite estiver perto.</span>
                    </div>
                 </div>
                 <div 
                    className={`w-11 h-6 rounded-full p-1 cursor-pointer transition-colors ${catFormAlert ? 'bg-brand-lime' : 'bg-slate-300 dark:bg-zinc-600'}`}
                    onClick={() => setCatFormAlert(!catFormAlert)}
                 >
                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${catFormAlert ? 'translate-x-5' : ''}`}></div>
                 </div>
              </div>

              <button 
                type="submit" 
                className="w-full bg-brand-dark dark:bg-white text-brand-lime dark:text-black py-4 rounded-xl font-bold text-lg hover:shadow-lg mt-4"
              >
                Salvar Categoria
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-zinc-900 w-full max-w-sm rounded-[2rem] p-6 animate-in zoom-in-95 duration-200 shadow-2xl text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500">
               <AlertTriangle size={32} />
            </div>
            
            <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Excluir Categoria?</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm leading-relaxed">
              Você tem certeza que deseja excluir esta categoria? As despesas associadas a ela <b>não serão excluídas</b>, mas ficarão "Sem categoria" no histórico.
            </p>

            <div className="flex gap-3">
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-3 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-slate-300 rounded-xl font-bold hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={confirmDeleteCategory}
                className="flex-1 py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-colors shadow-lg shadow-red-200 dark:shadow-red-500/20"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
